import { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { withRouter } from "react-router-dom";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
  CardNumberElement, CardExpiryElement, CardCvcElement
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import PaymentIcon from "@material-ui/icons/Payment";
import Button from "@material-ui/core/Button";
import Alert from "@material-ui/lab/Alert";
import Backdrop from "@material-ui/core/Backdrop";
import CircularProgress from "@material-ui/core/CircularProgress";
import CssBaseline from "@material-ui/core/CssBaseline";
import Container from "@material-ui/core/Container";
import { makeStyles } from "@material-ui/core/styles";

import Typography from "@material-ui/core/Typography";
import { cloudFunctions, auth } from "../../firebase";
import { countries } from "../../assets/country.json";
import * as Yup from "yup";
import { Formik } from "formik";
import amexCard from "../../assets/images/amex-card.png"
import discoverCard from "../../assets/images/discover-card.png"
import masterCard from "../../assets/images/master-card.png"
import visaCard from "../../assets/images/visa-card.png"
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const paymentSchema = Yup.object().shape({
  name: Yup.string()
      .min(2, "Too short")
      .max(50, "Too long")
      .required("Field Required"),
  country: Yup.string()
    .required('Country Field Required'),
  email: Yup.string().email("Invalid email address").required("Required field"),
});

const ELEMENT_OPTIONS = {
  style: {
    base: {
      color: "#32325d",
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: "antialiased",
      fontSize: "16px",
      "::placeholder": {
        color: "#aab7c4",
      },
    },
    invalid: {
      color: "#fa755a",
      iconColor: "#fa755a",
    },
  },
  hidePostalCode: true,
};

const useStyles = makeStyles((theme) => ({
  paper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  form: {
    width: "100%", // Fix IE 11 issue.
    marginTop: theme.spacing(3),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  
  alert: {
    margin: "10px 0px",
    width: "100%",
  },
}));

function StripePayment({selectedPlan}) {
  return (
    <Elements stripe={stripePromise}>
      <PaymentForm  selectedPlan={selectedPlan} />
    </Elements>
  );
}

function useInterval(callback, delay) {
  const savedCallback = useRef();

  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  useEffect(() => {
    let id = setInterval(() => {
      savedCallback.current();
    }, delay);
    return () => clearInterval(id);
  }, [delay]);
}

function PaymentForm({selectedPlan}) {
  const stripe = useStripe();
  const elements = useElements();
  const classes = useStyles();
  const { newUser = {} } = useSelector((state) => ({
    newUser: state.newUser,
  }));
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [cardError, setCardError] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [counter, setcounter] = useState(5);

  const initialValues = {
    email: newUser.email,
    name: newUser.name ? newUser.fullName:"",
    zipCode: "",
    state: "",
  }

  const handleSubmit = async (values) => {
    // Block native form submission.
    let hasError = false;
    let paymentMethodId = "";
    setLoading(true);
    setErrorMessage(null);
    setCardError(null);
    setSuccess(false);
    if (!stripe || !elements) {
      // Stripe.js has not loaded yet. Make sure to disable
      // form submission until Stripe.js has loaded.
      return;
    }

    // Get a reference to a mounted CardElement. Elements knows how
    // to find your CardElement because there can only ever be one of
    // each type of element.
    const cardElement = elements.getElement(CardNumberElement);

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: "card",
      card: cardElement,
    });

    if (error) {
      setLoading(false);
      setCardError(error.message);
      hasError = true;
    } else {
      paymentMethodId = paymentMethod.id;
    }

    if (!hasError) {
      const createSubscription = cloudFunctions.httpsCallable(
        "createSubscription"
      );
      createSubscription({
        planId: selectedPlan.id,
        accountId: newUser.uid,
        paymentMethodId: paymentMethodId,
        country: values.country,
        billingAddress: values
      })
        .then(async (res) => {
          setLoading(false);
          setSuccess(true);
        })
        .catch((err) => {
          setLoading(false);
          setErrorMessage(err.message);
        });
    }
  };

  useInterval(() => {
    if (success) {
      setcounter(counter - 1);
    }
  }, 1000);

  useEffect(() => {
    if (counter <= 0) {
      window.location.reload();
    }
  }, [counter]);

  return (
    <Container component="main" maxWidth="xs">
      <Backdrop open={loading} style={{ zIndex: 10000 }}>
        <CircularProgress style={{ width: 30, height: 30 }} color="inherit" />
      </Backdrop>
      <CssBaseline />
      <div className={classes.paper}>
        {success && (
          <Alert className={classes.alert} severity="success">
            The payment method has been successfully updated, you will be redirected to home page in {counter} seconds
          </Alert>
        )}
        {errorMessage !== null && (
          <Alert className={classes.alert} severity="error">
            {errorMessage}
          </Alert>
        )}
        {cardError !== null && (
          <Alert className={classes.alert} severity="error">
            {cardError}
          </Alert>
        )}
        {loading && 
          <Typography align="center" component="h6" variant="h6">
          Your payment is getting processed... please wait
          </Typography>
        }
       {!success && 
        <Formik onSubmit={handleSubmit} initialValues={initialValues} validationSchema={paymentSchema}>
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
          }) => (
            <form onSubmit={handleSubmit} className="form-container">
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="text"
                  name="email"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.email}
                  disabled
                />
                <div className="field-error">{errors.email && touched.email && errors.email}</div>
              </div>
              <div className="form-group card-infos billing-addresss">
                <label htmlFor="name">Card information</label>
                <div className="cardNumber">
                  <CardNumberElement options={ELEMENT_OPTIONS} />
                  <div className="cards-images">
                    <img src={visaCard} alt="visaCard" />
                    <img src={masterCard} alt="masterCard" />
                    <img src={amexCard} alt="amexCard" />
                    <img src={discoverCard} alt="discoverCard" />
                  </div>
                </div>
                <div className="cvv-date">
                  <CardExpiryElement options={ELEMENT_OPTIONS} />
                  <CardCvcElement options={ELEMENT_OPTIONS} />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="name">Name on Card</label>
                <input
                  type="text"
                  name="name"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.name}
                />
                <div className="field-error">{errors.name && touched.name && errors.name}</div>
              </div>
              <div className="form-group billing-addresss">
                {/*<label htmlFor="country">Billing address</label>*/}
                <select
                  type="text"
                  name="country"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.country}
                  placeholder="Select Your Country"
                >
                  <option value="">Select Your Country</option>
                  {Object.keys(countries).map((countryCode) => (
                    <option value={countryCode} key={countryCode}>
                      {countries[countryCode].name}
                    </option>
                  ))}
                </select>
                {/*
                <input
                  type="text"
                  name="address1"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.address}
                  placeholder="Address"
                />
                <div className="city-zip">
                  <input
                    type="text"
                    name="city"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.city}
                    placeholder="City"
                  />
                  <input
                    type="text"
                    name="zip"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.zip}
                    placeholder="ZIP"
                  />
                </div>
                <input
                  type="text"
                  name="state"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.state}
                  placeholder="State"
                />
                */}
                <div className="field-error">{errors.country && touched.country && errors.country}</div>
              </div>
              
              <Button
                variant="contained"
                color="default"
                fullWidth
                disabled={success}
                onClick={handleSubmit}
                startIcon={<PaymentIcon />}
              >
                Checkout
              </Button>
            </form>
          )}
        </Formik>
        } 
      </div>
    </Container>
  );
}

export default StripePayment;
